import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/dialog";
import { Button } from "../../components/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../components/select";
import { Input } from "../../components/input";
import { Label } from "../../components/label";

interface DropdownOption {
  id: string;
  name: string;
  status: string;
}

interface ProductMasterFormProps {
  onSubmit: (data: any) => void;
  initialValues?: any;
  groupValues?: DropdownOption[];
  subGroupValues?: DropdownOption[];
  subSubGroupValues?: DropdownOption[];
  shapeSizeValues?: DropdownOption[];
  mainCategoryValues?: DropdownOption[];
  subCategoryValues?: DropdownOption[];
  collectionValues?: DropdownOption[];
  fetchSubGroups: (groupId: string) => Promise<DropdownOption[]>;
  fetchSubSubGroups: (subGroupId: string) => Promise<DropdownOption[]>;
  isEdit?: boolean;
}

interface VariantRow {
  group: string;
  subGroup: string;
  subSubGroup: string;
  shape: string;
  size: string;
  stones: number;
  weight: string;
  availableSubGroups: Array<{ sub_group_id: string; name: string; status: string }>;
  availableSubSubGroups: Array<{ sub_sub_group_id: string; name: string; status: string }>;
}

const createNewRow = (): VariantRow => ({
  group: "",
  subGroup: "",
  subSubGroup: "",
  shape: "",
  size: "",
  stones: 0,
  weight: "",
  availableSubGroups: [],
  availableSubSubGroups: []
})

export function ProductDetailModal({
  data,
  initialValues = {},
  groupValues = [],
  shapeSizeValues = [],
  fetchSubGroups,
  fetchSubSubGroups,
  onClose,
  onSave,
}: {
  data: any;
  initialValues?: any;
  groupValues?: any[];
  shapeSizeValues?: any[];
  fetchSubGroups: (groupId: string) => Promise<DropdownOption[]>;
  fetchSubSubGroups: (subGroupId: string) => Promise<DropdownOption[]>;
  onClose: () => void;
  onSave: () => void;
}) {
  const [rows, setRows] = useState([createNewRow()])

  // Update the row change handlers
  const handleGroupChange = async (index: number, groupId: string) => {
    const subGroups = await fetchSubGroups(groupId);
    
    setRows(prev => {
      const updated = [...prev];
      updated[index] = { 
        ...updated[index], 
        group: groupId,
        subGroup: "", // Reset subgroup when group changes
        subSubGroup: "", // Reset sub-subgroup
        availableSubGroups: subGroups,
        availableSubSubGroups: [] // Clear sub-subgroups
      };
      return updated;
    });
  };

  const handleSubGroupChange = async (index: number, subGroupId: string) => {
    const subSubGroups = await fetchSubSubGroups(subGroupId);
    
    setRows(prev => {
      const updated = [...prev];
      updated[index] = { 
        ...updated[index], 
        subGroup: subGroupId,
        subSubGroup: "", // Reset sub-subgroup
        availableSubSubGroups: subSubGroups 
      };
      return updated;
    });
  };

  const handleRowChange = (index: number, field: keyof VariantRow, value: any) => {
    setRows(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleAddRow = () => setRows((prev) => [...prev, createNewRow()])
  const handleDeleteRow = (index) => setRows((prev) => prev.filter((_, i) => i !== index))

  useEffect(() => {
    if (Array.isArray(initialValues?.variants)) {
      const loadDependentData = async () => {
        const variantsWithData = await Promise.all(
          initialValues.variants.map(async (variant) => {
            // Fetch subgroups for this variant's group
            const availableSubGroups = variant.group 
              ? await fetchSubGroups(variant.group)
              : [];
            
            // Fetch sub-subgroups for this variant's subgroup
            const availableSubSubGroups = variant.subGroup 
              ? await fetchSubSubGroups(variant.subGroup)
              : [];
  
            return {
              ...variant,
              availableSubGroups,
              availableSubSubGroups
            };
          })
        );
        setRows(variantsWithData.length ? variantsWithData : [createNewRow()]);
      };
  
      loadDependentData();
    }
  }, [initialValues, fetchSubGroups, fetchSubSubGroups]);

  return (
    <Dialog open={true} onOpenChange={onClose} >
      <DialogContent style={{ minWidth: '70%' }} className="w-[95vw] h-[95vh] max-w-none max-h-none overflow-auto p-6">
        <DialogHeader>
          <DialogTitle>Product Details</DialogTitle>
        </DialogHeader>

        {/* <div className="space-y-2">
          <p><strong>Customer:</strong> {data.customer}</p>
          <p><strong>No of Variants:</strong> {data.no_of_variants}</p>
          <p><strong>Design:</strong> {data.design}</p>
          <p><strong>Date:</strong> {new Date(data.date).toLocaleDateString("en-GB")}</p>
          <p><strong>Main Category:</strong> {data.main_category}</p>
        </div> */}

        {/* Variants Table */}
        <div className="border rounded-lg p-4 mt-6">
          <Label className="mb-2 block">Product Variants</Label>
          <table className="w-full border text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th>Group</th>
                <th>Sub Group</th>
                <th>Sub Sub Group</th>
                <th>Shape</th>
                <th>Size</th>
                <th>No. of Stones</th>
                <th>Total Weight</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={index}>
                  <td>
                    <Select
                      value={row.group}
                      onValueChange={(val) => handleGroupChange(index, val)}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            groupValues.find((g) => String(g.group_id) === String(row.group))?.name || "Select Group"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {groupValues.map((g) => (
                          <SelectItem key={g.group_id} value={String(g.group_id)}>
                            {g.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td>
                    <Select
                      value={String(row.subGroup ?? "")}
                      onValueChange={(val) => handleSubGroupChange(index, val)}
                      disabled={!row.group || row.availableSubGroups.length === 0}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            row.availableSubGroups.find((sg: any) => String(sg.sub_group_id) === String(row.subGroup))
                              ?.name || "Select Sub Group"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {row.availableSubGroups.map((sg: any) => (
                          <SelectItem key={sg.sub_group_id} value={String(sg.sub_group_id)}>
                            {sg.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td>
                    <Select
                      value={String(row.subSubGroup ?? "")}
                      onValueChange={(val) => handleRowChange(index, "subSubGroup", val)}
                      disabled={!row.subGroup || row.availableSubSubGroups.length === 0}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            row.availableSubSubGroups.find(
                              (ssg: any) => String(ssg.sub_sub_group_id) === String(row.subSubGroup)
                            )?.name || "Select Sub Sub Group"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {row.availableSubSubGroups.map((ssg: any) => (
                          <SelectItem key={ssg.sub_sub_group_id} value={String(ssg.sub_sub_group_id)}>
                            {ssg.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td>
                    {shapeSizeValues?.length > 0 && (
                      <Select
                        value={row.shape}
                        onValueChange={(val) => handleRowChange(index, "shape", val)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select Shape" />
                        </SelectTrigger>
                        <SelectContent>
                          {shapeSizeValues.map((shape) => (
                            <SelectItem key={shape.shape} value={shape.shape}>
                              {shape.shape}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </td>
                  <td>
                    <Input
                      value={row.size}
                      onChange={(e) => handleRowChange(index, "size", e.target.value)}
                    />
                  </td>
                  <td>
                    <Input
                      type="number"
                      value={row.stones}
                      onChange={(e) => handleRowChange(index, "stones", Number(e.target.value))}
                    />
                  </td>
                  <td>
                    <Input
                      value={row.weight}
                      onChange={(e) => handleRowChange(index, "weight", e.target.value)}
                    />
                  </td>
                  <td>
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() => handleDeleteRow(index)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-4">
            <Button type="button" onClick={handleAddRow}>
              Add Row
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={onSave}>Save</Button>
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
